import UserModalHeader from '../../../../Component/UI/UserModalHeader';
import React, { useEffect, useRef, useState } from "react";
import api from "../../../../Component/axios_Interceptor/api";
import url from "../../../../Component/server";
import styled, { keyframes } from "styled-components";
import {  RowView } from "../../../../Component/style/common_style";

interface Buy_Cancel_Modal_Props {
  visible: boolean;
  handleModal: (visible: boolean) => void;
  item: {
    product_num: number | string | null;
    product_name: string;
    option_name: string;
    order_cnt: number;
    seq: number;
  } | null;
  items?: NonNullable<Buy_Cancel_Modal_Props["item"]>[];
  ticketCount: number;
  orderSeq: number;
  liveSeq: number;
  storeSeq: number;
  onCancelSuccess?: () => void;
}

const slideUp = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideDown = keyframes`
  from {
    transform: translateY(0);
    opacity: 1;
  }
  to {
    transform: translateY(100%);
    opacity: 0;
  }
`;

const Buy_Cancel_Modal = ({ visible, handleModal, item, items, onCancelSuccess, orderSeq, liveSeq, storeSeq, ticketCount }: Buy_Cancel_Modal_Props) => {
  const selectedItem = item;
  const cancelItems = items?.length ? items : item ? [item] : [];
  const totalCount = cancelItems.reduce((sum, entry) => sum + Number(entry.order_cnt), 0);
  const maxCancelCount = Math.min(totalCount, Math.max(0, ticketCount));
  const [cancelCount, setCancelCount] = useState(1);
  const [busy, setBusy] = useState(false);
  const [completed, setCompleted] = useState(false);
  const closeRef = useRef(handleModal);
  closeRef.current = handleModal;

  useEffect(() => {
    if (visible) {
      setCancelCount(maxCancelCount > 0 ? 1 : 0);
      setCompleted(false);
    }
  }, [visible, selectedItem?.seq, totalCount, maxCancelCount]);

  useEffect(() => {
    if (!visible || !completed) return;
    const timer = window.setTimeout(() => {
      setCompleted(false);
      closeRef.current(false);
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [visible, completed]);

  const closeModal = () => {
    if (busy) return;
    setCompleted(false);
    handleModal(false);
  };

  if (!visible || !selectedItem) return null;

  const handleDecrease = () => {
    if (!busy && cancelCount > 1) {
      setCancelCount(cancelCount - 1);
    }
  };

  const handleIncrease = () => {
    if (!busy && cancelCount < maxCancelCount) {
      setCancelCount(cancelCount + 1);
    }
  };

  const handleCancelSubmit = async () => {
    if (busy || completed) return;
    if (cancelCount === 0 || cancelCount > maxCancelCount) {
      alert("취소할 수량을 선택해주세요.");
      return;
    }
    if (!orderSeq || !storeSeq || !liveSeq) { alert("주문 정보를 다시 불러와주세요."); return; }
    setBusy(true);
    try {
      await api.post(url.Communication_url.orders_cancel, { orderSeq, detailSeq: selectedItem.seq, ...(cancelItems.length > 1 ? { detailSeqs: cancelItems.map(entry => entry.seq) } : {}), orderCnt: cancelCount, storeSeq, liveSeq });
      setCompleted(true);
      onCancelSuccess?.();
    } catch (error: any) {
      alert(error?.response?.data?.message || "주문 취소에 실패했습니다.");
    } finally { setBusy(false); }
  };

  if (completed) return (
    <BackgroundArea $visible={visible} $isOpen={visible}
      onClick={event => { event.stopPropagation(); if (event.target === event.currentTarget) closeModal(); }}>
      <CompleteBox $isOpen={visible} role="dialog" aria-modal="true" aria-label="주문 취소 완료"
        onClick={event => event.stopPropagation()}>
        <Title_Box>주문취소 완료</Title_Box>
        <CompleteMessage role="status">주문이 취소되었습니다.</CompleteMessage>
        <CancelSubmitBtn type="button" autoFocus onClick={closeModal}>확인</CancelSubmitBtn>
      </CompleteBox>
    </BackgroundArea>
  );

  return (
    <BackgroundArea $visible={visible} $isOpen={visible}
      onClick={event => { event.stopPropagation(); if (event.target === event.currentTarget) closeModal(); }}>
      <ModalBox $isOpen={visible} role="dialog" aria-modal="true" aria-label="주문취소" onClick={(e) => e.stopPropagation()}>
        <UserModalHeader title="주문취소" onClose={closeModal} />
        <Content_Box>
          {/* 상품 정보 박스 */}
          <InfoCard>
            <CardLeft>
              <ProductName>{selectedItem.product_name}</ProductName>
              <ProductOption>· {selectedItem.option_name || "단일옵션"} / {totalCount}개</ProductOption>
            </CardLeft>
            <CardRight>No. {selectedItem.product_num}</CardRight>
          </InfoCard>

          {/* 취소 수량 섹션 */}
          <SectionLabel style={{ marginTop: 30 }}>취소 수량</SectionLabel>
          <CouponUsage>상품 1개당 쿠폰 1장 · 보유 {ticketCount}장 · 사용 {cancelCount}장</CouponUsage>
          <ControlRow>
            <Counter>
              <Counter_Btn type="button" onClick={handleDecrease} disabled={busy || cancelCount <= 1}>
                <img alt="취소 수량 줄이기" src={require("../../../../Image/ic_minus_black.png")} />
              </Counter_Btn>
              <span aria-label="취소 수량">{cancelCount}</span>
              <Counter_Btn type="button" onClick={handleIncrease} disabled={busy || cancelCount >= maxCancelCount}>
                <img alt="취소 수량 늘리기" src={require("../../../../Image/ic_plus_black.png")} />
              </Counter_Btn>
            </Counter>

            <Total_Count>
              <div>취소 후 수량</div>
              <RowView className="count_text">
                <div className="count">{totalCount - cancelCount}</div>개
              </RowView>
            </Total_Count>
          </ControlRow>
        </Content_Box>
        <CancelSubmitBtn type="button" disabled={busy} onClick={handleCancelSubmit}>{busy ? "취소 중…" : "취소하기"}</CancelSubmitBtn>
      </ModalBox>
    </BackgroundArea>
  );
};

export default Buy_Cancel_Modal;

const BackgroundArea = styled.div<{ $visible: boolean; $isOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, .32);
  position: absolute;
  inset: 0;
  display: ${(props) => (props.$visible ? "flex" : "none")};
  justify-content: center;
  align-items: center;
  opacity: ${(props) => (props.$isOpen ? 1 : 0)};
  z-index: 999;
`;

const ModalBox = styled.div<{ $isOpen: boolean }>`
  box-sizing: border-box;
  width: 100%;
  max-width: 500px;
  background-color: var(--user-surface);
  border-top-right-radius: 16px;
  border-top-left-radius: 16px;
  position: absolute;
  bottom: 0px;
  animation: ${(props) => (props.$isOpen ? slideUp : slideDown)} 0.3s ease-out;
  max-height: calc(100dvh - 32px);
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: 24px;
`;

const Title_Box = styled.div`
  width: 100%;
  height: 70px;
  display: flex;
  position: relative;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  font-family: var(--font-Pretendard-Bold);
  border-bottom: 1px solid var(--user-border);
  color: var(--user-text);
`;

const Content_Box = styled.div`
  width: 100%;
  padding: 24px 20px;
  display: flex;
  box-sizing: border-box;
  flex-direction: column;
`;

const InfoCard = styled(RowView)`
  background-color: var(--user-surface-raised);
  border-radius: 8px;
  padding: 16px;
  box-sizing: border-box;
  justify-content: space-between;
  align-items: center;
`;

const CardLeft = styled.div`
  min-width: 0;
  overflow-wrap: anywhere;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const ProductName = styled.div`
  font-size: 16px;
  font-family: var(--font-Pretendard-Bold);
  color: var(--user-text);
`;

const ProductOption = styled.div`
  font-size: 14px;
  color: var(--user-text-muted);
  font-family: var(--font-Pretendard-Regular);
`;

const CardRight = styled.div`
  flex-shrink: 0;
  margin-left: 12px;
  font-size: 14px;
  color: var(--user-text-muted);
  font-family: var(--font-Pretendard-Regular);
`;

const SectionLabel = styled.div`
  font-size: 15px;
  font-family: var(--font-Pretendard-Bold);
  color: var(--user-text);
  margin-bottom: 12px;
`;

const ControlRow = styled(RowView)`
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin-top: 10px;
`;

const Counter = styled.div`
  padding: 4px 8px;
  width: min(55%, 220px);
  display: flex;
  position: relative;
  box-sizing: border-box;
  border: 1px solid var(--user-border);
  border-radius: 8px;
  justify-content: space-between;
  align-items: center;
  font-size: 20px;
  color: var(--user-text);
  font-family: var(--font-Pretendard-Bold);
`;

const Counter_Btn = styled.button`
  display: grid;
  place-items: center;
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  border: 0;
  padding: 0;
  background: transparent;
  cursor: pointer;
  img { width: 20px; height: 20px; filter: var(--user-icon-filter); }
  &:disabled { opacity: .4; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
`;

const Total_Count = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid var(--user-border);
  width: 35%;
  font-size: 14px;
  color: var(--user-text-muted);
  font-family: var(--font-Pretendard-Regular);
  padding-bottom: 4px;

  div.count {
    color: var(--user-error);
    font-size: 24px;
    font-family: var(--font-Pretendard-Bold);
  }
  div.count_text {
    color: var(--user-text);
    font-size: 24px;
    font-family: var(--font-Pretendard-Bold);
    display: flex;
    align-items: center;
  }
`;

const CancelSubmitBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 0;
  background: linear-gradient(135deg, #7955F0, #3209B9);
  color: white;
  font-size: 16px;
  font-family: var(--font-Pretendard-SemiBold);
  width: calc(100% - 40px);
  height: 52px;
  border-radius: 10px;
  cursor: pointer;
  margin: 0 auto;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.15);
  margin-top: 10px;
  &:active {
    opacity: 0.8;
  }
  &:disabled { opacity: 0.6; cursor: not-allowed; }
  &:focus-visible { outline: 2px solid var(--user-accent); outline-offset: 2px; }
`;

const CompleteBox = styled(ModalBox)`
  background: var(--user-surface-raised);
  border: 1px solid var(--user-border);
  box-shadow: 0 16px 48px rgba(0, 0, 0, .3);
  position: relative;
  bottom: auto;
  width: calc(100% - 40px);
  max-width: 432px;
  border-radius: 24px;
`;

const CompleteMessage = styled.div`
  padding: 28px 20px;
  color: var(--user-text);
  font-size: 16px;
  text-align: center;
`;

const CouponUsage = styled.p`margin: 8px 0 12px; color: var(--user-text-muted); font-size: 14px; line-height: 1.5;`;
